import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaWhatsapp,
  FaExclamationTriangle,
  FaPhone,
  FaUser,
  FaStar,
  FaCoins,
  FaEye,
  FaTag,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { Customer, WhatsAppMessageTemplate, Promotion } from "../../types/loyalty.types";
import { useWhatsAppMessaging } from "../../hooks/useWhatsAppMessaging";
import { getWhatsAppMessagePreview } from "../../utils/whatsappHelper";
import { promotionsApi } from "../../api/backend-loyalty.api";
import { jwtDecode } from "jwt-decode";


interface QuickWhatsAppSenderModalProps {
  isOpen: boolean;
  customer: Customer | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const QuickWhatsAppSenderModal: React.FC<QuickWhatsAppSenderModalProps> = ({
  isOpen,
  customer,
  onClose,
  onSuccess,
}) => {
  const [selectedTemplate, setSelectedTemplate] =
    useState<WhatsAppMessageTemplate | null>(null);
  const [customMessage, setCustomMessage] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New State for Promotions
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(null);
  const [loadingPromotions, setLoadingPromotions] = useState(false);

  const {
    templates,
    autoFillTemplateVariables,
    sendWhatsAppMessage,
    logMessageSent,
    restaurantData,
  } = useWhatsAppMessaging();

  // Fetch Promotions
  useEffect(() => {
    const fetchPromotions = async () => {
      if (!isOpen || !restaurantData?.id) return;
      
      const token = localStorage.getItem("authToken");
      if (!token) return;

      try {
        setLoadingPromotions(true);
        // Using getCampaigns as a proxy for getPromotions
        const response = await promotionsApi.getCampaigns(token, restaurantData.id);
        
        let fetchedPromotions: Promotion[] = [];
        // Handle various response shapes
        if (Array.isArray(response)) {
            fetchedPromotions = response;
        } else if (response && Array.isArray((response as any).data)) {
            fetchedPromotions = (response as any).data;
        } else if (response && Array.isArray((response as any).content)) {
             fetchedPromotions = (response as any).content;
        } else if (response && Array.isArray((response as any).items)) {
             fetchedPromotions = (response as any).items;
        }

        // Filter active promotions
        const activePromos = fetchedPromotions.filter((p: any) => p.isActive !== false);
        setPromotions(activePromos);
      } catch (err) {
        console.warn("Failed to load promotions:", err);
        // Don't show error to user, just don't show promotions
      } finally {
        setLoadingPromotions(false);
      }
    };

    fetchPromotions();
  }, [isOpen, restaurantData?.id]);


  const activeTemplates = templates.filter((t) => t.isActive);

  // Update preview when template or custom message changes
  useEffect(() => {
    if (!customer) return;

    if (useCustom && customMessage) {
      getWhatsAppMessagePreview(customMessage);
      setPreview(customMessage);
    } else if (selectedTemplate && !useCustom) {
      // 🔥 NEW: Use auto-fill instead of old prepareCustomerMessage
      try {
        const filledMessage = autoFillTemplateVariables(
          selectedTemplate.content,
          customer
        );
        setPreview(filledMessage);
        setError(null);
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "Failed to prepare message";
        setError(errorMsg);
        setPreview("");
      }
    }
  }, [
    selectedTemplate,
    customMessage,
    useCustom,
    customer,
    autoFillTemplateVariables,
  ]);

  const handleSendMessage = async () => {
    if (!customer) return;

    const messageToSend = useCustom ? customMessage : preview;

    if (!messageToSend) {
      toast.error("Please select a template or enter a custom message");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Validate phone number
      if (!customer.phone || !customer.phone.match(/^[\d+\-\s()]+$/)) {
        throw new Error("Invalid phone number");
      }

      // Send the message (opens WhatsApp)
      const success = sendWhatsAppMessage(customer.phone, messageToSend);

      if (success) {
        // Log the message
        await logMessageSent(
          customer.id,
          customer.name,
          customer.phone,
          selectedTemplate?.id || "custom",
          messageToSend
        );

        toast.success("WhatsApp opened with pre-filled message");

        // Small delay to show success state
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 1500);
      } else {
        throw new Error("Failed to open WhatsApp");
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to send message";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  if (!customer) return null;

  const isDisabled =
    loading ||
    !preview ||
    (useCustom && !customMessage.trim()) ||
    (!useCustom && !selectedTemplate);

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => { if (e.target === e.currentTarget) onClose(); }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 16 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[88vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-green-500 to-green-600 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
                  <FaWhatsapp className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white leading-tight">Send WhatsApp Message</h2>
                  <p className="text-[11px] text-green-100">to {customer.name}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                title="Close"
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/25 transition-colors text-white"
              >
                <FaTimes className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Customer Info Bar */}
            <div className="px-5 py-3 bg-green-50 border-b border-green-100 shrink-0">
              <div className="grid grid-cols-4 gap-2">
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Name</span>
                  <span className="text-xs font-semibold text-gray-800 truncate">{customer.name}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Phone</span>
                  <span className="text-xs font-semibold text-gray-800 truncate">{customer.phone}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Tier</span>
                  <span className="text-xs font-semibold truncate" style={{ color: customer.loyaltyTier?.color ?? '#6B7280' }}>
                    {customer.loyaltyTier?.name ?? '—'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Points</span>
                  <span className="text-xs font-semibold text-orange-600">{customer.points}</span>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4 min-h-0">
              {/* Error */}
              {error && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl p-3">
                  <FaExclamationTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 font-medium">{error}</p>
                </div>
              )}

              {/* Tab Switcher */}
              <div className="flex items-center bg-gray-100 rounded-xl p-1 gap-1">
                <button
                  onClick={() => { setUseCustom(false); setError(null); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                    !useCustom ? "bg-white text-green-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Templates
                </button>
                <button
                  onClick={() => { setUseCustom(true); setError(null); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                    useCustom ? "bg-white text-green-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Custom Message
                </button>
              </div>

              {/* Templates */}
              {!useCustom && (
                <div className="space-y-2">
                  {activeTemplates.length === 0 ? (
                    <div className="text-center py-8 text-xs text-gray-500 border border-dashed border-gray-200 rounded-xl">
                      No active templates found. Create one in Rewards &amp; Templates.
                    </div>
                  ) : (
                    activeTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => { setSelectedTemplate(template); setError(null); }}
                        className={`w-full px-4 py-3 rounded-xl text-left transition-all border ${
                          selectedTemplate?.id === template.id
                            ? "border-green-500 bg-green-50 ring-1 ring-green-300"
                            : "border-gray-200 hover:border-green-300 hover:bg-gray-50 bg-white"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-gray-900">{template.name}</span>
                          <span className="text-[10px] font-medium bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full border border-blue-100">
                            {template.type}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-500 line-clamp-2">
                          {template.description || template.content}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* Custom Message */}
              {useCustom && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Your Message</label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => { setCustomMessage(e.target.value); setError(null); }}
                    placeholder="Type your WhatsApp message here..."
                    rows={4}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500/30 focus:border-green-400 outline-none resize-none transition"
                  />
                  <p className="text-[10px] text-gray-400 mt-1 text-right">{customMessage.length} / 4096</p>
                </div>
              )}

              {/* Preview */}
              {preview && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl border border-green-200 bg-green-50 p-3"
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <FaEye className="w-3 h-3 text-green-600" />
                    <span className="text-[11px] font-semibold text-green-800">Message Preview</span>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-xs text-gray-800 whitespace-pre-wrap max-h-28 overflow-y-auto border border-green-100 leading-relaxed">
                    {preview}
                  </div>
                </motion.div>
              )}

              {/* Info */}
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3">
                <span className="text-blue-400 text-xs mt-0.5">ℹ</span>
                <p className="text-[11px] text-blue-700 leading-relaxed">
                  When you click "Send Message", WhatsApp will open with the message pre-filled.
                  You'll need to tap Send in WhatsApp to complete the action.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={handleSendMessage}
                disabled={isDisabled}
                className={`px-5 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all ${
                  isDisabled
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow-md"
                }`}
              >
                {loading ? (
                  <>
                    <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    <span>Opening...</span>
                  </>
                ) : (
                  <>
                    <FaWhatsapp className="w-3.5 h-3.5" />
                    <span>Send Message</span>
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default QuickWhatsAppSenderModal;
